import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { fetchRouteRollout, type RouteRolloutRow } from "../data/rollout";
import { useReadModel } from "../data/useReadModel";
import { useTokenMintSession } from "./token-mint-session";

// Rollout Status surface (read-only) — route readiness cards, needs-attention first. Surfaces the
// "what needs installing/fixing" lane: ready/required, needs-setup, and token/geofence blockers.
export function RolloutStatus({
  client,
  accountId,
  canMint = false,
}: {
  client: SupabaseClient;
  accountId: string;
  canMint?: boolean;
}) {
  const session = useTokenMintSession();
  const mintEnabled = canMint && session?.enabled === true;
  const mintGeneration = session?.mintGeneration ?? 0;

  const { status, data, error } = useReadModel<RouteRolloutRow[]>(
    () => fetchRouteRollout(client, accountId),
    [client, accountId, mintGeneration],
  );

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading rollout status…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load rollout status."}
      </p>
    );
  }

  const rows = [...(data ?? [])].sort(
    (left, right) => (left.rolloutPercent ?? 0) - (right.rolloutPercent ?? 0),
  );
  if (rows.length === 0) {
    return <p className="surface__state" role="status">No routes to roll out yet.</p>;
  }

  return (
    <div className="rollout">
      {rows.map((row, index) => {
        const pct = Math.max(0, Math.min(100, row.rolloutPercent ?? 0));
        return (
          <article key={row.routeId || `${row.routeName ?? "route"}-${index}`} className="rollout__card">
            <header className="rollout__head">
              <span className="rollout__route">{row.routeName ?? "Route"}</span>
              <span className={`rollout__badge rollout__badge--${row.rolloutStatus}`}>
                {humanizeLabel(row.rolloutStatus)}
              </span>
            </header>
            <div className="rollout__where">
              {[row.clientName, row.siteName].filter(Boolean).join(" · ") || "—"}
            </div>
            <div
              className="rollout__bar"
              role="progressbar"
              aria-valuenow={pct}
              aria-valuemin={0}
              aria-valuemax={100}
            >
              <div className="rollout__bar-fill" style={{ width: `${pct}%` }} />
            </div>
            <div className="rollout__meta">
              {row.readyCount}/{row.requiredCount} required ready
              {row.needsSetupCount > 0 ? ` · ${row.needsSetupCount} need setup` : ""}
            </div>
            {row.missingMaterialCount > 0 || row.missingQrCount > 0 || row.missingNfcCount > 0 || row.missingGeofenceCount > 0 ? (
              <div className="rollout__blockers">
                {row.missingMaterialCount > 0 ? (
                  <span className="rollout__chip">Missing material ×{row.missingMaterialCount}</span>
                ) : null}
                {row.missingQrCount > 0 ? (
                  <span className="rollout__chip">Missing QR ×{row.missingQrCount}</span>
                ) : null}
                {row.missingNfcCount > 0 ? (
                  <span className="rollout__chip">Missing NFC ×{row.missingNfcCount}</span>
                ) : null}
                {row.missingGeofenceCount > 0 ? (
                  <span className="rollout__chip">No geofence ×{row.missingGeofenceCount}</span>
                ) : null}
              </div>
            ) : null}
            {mintEnabled && row.routeId ? (
              <div className="rollout__actions">
                {session!.hasRouteSessionKit(row.routeId) ? (
                  <button
                    type="button"
                    className="builder__navbtn"
                    aria-label={`View kit for ${row.routeName ?? "route"}`}
                    onClick={() => session!.openRouteSessionKit(row.routeId)}
                  >
                    View kit
                  </button>
                ) : null}
                {row.missingMaterialCount > 0 ? (
                  <button
                    type="button"
                    className="minter__confirm-go"
                    onClick={() =>
                      session!.openRouteConfirm({
                        routeId: row.routeId,
                        routeName: row.routeName ?? "Route",
                        replace: false,
                        hasActiveMaterial: false,
                      })
                    }
                  >
                    Mint kit
                  </button>
                ) : null}
                {row.missingMaterialCount === 0 && row.requiredCount > 0 ? (
                  <button
                    type="button"
                    className="builder__navbtn"
                    onClick={() =>
                      session!.openRouteConfirm({
                        routeId: row.routeId,
                        routeName: row.routeName ?? "Route",
                        replace: true,
                        hasActiveMaterial: true,
                      })
                    }
                  >
                    Replace tokens
                  </button>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </div>
  );
}
